import { Injectable } from '@angular/core';
import { IBase64Response, PrintService } from '@app/core/services/print.service';
import { Base64UrlService } from './base64url.service';
import { DataTaskService } from '@app/core/services/data-task.service';
import { decodeFromBase64DataUri, degrees, PageSizes, PDFAnnotation, PDFArray, PDFDict, PDFDocument, PDFFont, PDFHexString, PDFName, PDFNumber, PDFPage, PDFPageDrawTextOptions, PDFRef, PDFString, RGB, rgb, StandardFonts } from 'pdf-lib';

/**
 * Service for pdf-handling.
 */
@Injectable({
    providedIn: 'root',
})
export class PdfService {
    private A4_PAGE_WIDTH = PageSizes.A4[0];

    private A4_PAGE_HEIGHT = PageSizes.A4[1];

    constructor(
        private printService: PrintService,
        private dataTaskService: DataTaskService,
        private base64: Base64UrlService,
    ) {}

    public async preview(parameters: { reportKeyno: number; parameters: any }) {
        const p2ReportDef = (await this.loadP2ReportDef(parameters.reportKeyno))[0];
        const htmlReport = (await this.loadHtmlReportHtml(p2ReportDef.htmlreport_keyno, parameters.parameters))[0];

        htmlReport.filename = this.base64.urlDecode(htmlReport.filename);
        htmlReport.headerstring = this.base64.urlDecode(htmlReport.headerstring);
        htmlReport.htmlstring = this.base64.urlDecode(htmlReport.htmlstring);
        htmlReport.footerstring = this.base64.urlDecode(htmlReport.footerstring);

        const printParameters = {
            download: true,
            filename: htmlReport.filename,
            sourceHtml: htmlReport.htmlstring,
            printerProperties: {
                isLandscape: p2ReportDef.isLandscape,
                copies: 1,
                pageAutoSize: p2ReportDef.paper_size_id === 'standard',
                paperSize: {
                    height: p2ReportDef.height ?? 0,
                    width: p2ReportDef.width ?? 0,
                },
            },
            pdfOptions: {
                marginOptions: { bottom: '12.7mm', top: '12.7mm', left: '12.7mm', right: '12.7mm' },
                landscape: p2ReportDef.print_landscape == '1' ? true : false,
                outline: false,
                printBackground: true,
                displayHeaderFooter: true,
                headerTemplate: htmlReport.headerstring,
                footerTemplate: htmlReport.footerstring,
            },
        };
        if (p2ReportDef.paper_size_id !== 'standard' && p2ReportDef.height > 0 && p2ReportDef.width > 0) {
            //@ts-ignore
            printParameters.pdfOptions.height = `${p2ReportDef.height}mm`;
            //@ts-ignore
            printParameters.pdfOptions.width = `${p2ReportDef.width}mm`;

            printParameters.pdfOptions.marginOptions = { bottom: '5mm', top: '5mm', left: '5mm', right: '5mm' };
        }
        if (p2ReportDef.custom_margins == 1) {
            printParameters.pdfOptions.marginOptions = {
                top: `${p2ReportDef.margin_top}mm`,
                right: `${p2ReportDef.margin_right}mm`,
                bottom: `${p2ReportDef.margin_bottom}mm`,
                left: `${p2ReportDef.margin_left}mm`,
            };
        }
        return URL.createObjectURL((await this.htmlToPdfBlob(htmlReport.htmlstring, printParameters)).binaryData);
    }

    public async getAttachment(parameters: { reportKeyno: number; parameters: any }) {
        const p2ReportDef = (await this.loadP2ReportDef(parameters.reportKeyno))[0];
        const htmlReport = (await this.loadHtmlReportHtml(p2ReportDef.htmlreport_keyno, parameters.parameters))[0];

        htmlReport.filename = this.base64.urlDecode(htmlReport.filename);
        htmlReport.headerstring = this.base64.urlDecode(htmlReport.headerstring);
        htmlReport.htmlstring = this.base64.urlDecode(htmlReport.htmlstring);
        htmlReport.footerstring = this.base64.urlDecode(htmlReport.footerstring);

        const printParameters = {
            download: true,
            filename: htmlReport.filename,
            saveToFileServer: true,
            sourceHtml: htmlReport.htmlstring,
            printerProperties: {
                isLandscape: p2ReportDef.isLandscape,
                copies: 1,
                pageAutoSize: p2ReportDef.paper_size_id === 'standard',
                paperSize: {
                    height: p2ReportDef.height ?? 0,
                    width: p2ReportDef.width ?? 0,
                },
            },
            pdfOptions: {
                marginOptions: { bottom: '12.7mm', top: '12.7mm', left: '12.7mm', right: '12.7mm' },
                landscape: p2ReportDef.print_landscape == '1' ? true : false,
                outline: false,
                printBackground: true,
                displayHeaderFooter: true,
                headerTemplate: htmlReport.headerstring,
                footerTemplate: htmlReport.footerstring,
            },
        };
        if (p2ReportDef.paper_size_id !== 'standard' && p2ReportDef.height > 0 && p2ReportDef.width > 0) {
            //@ts-ignore
            printParameters.pdfOptions.height = `${p2ReportDef.height}mm`;
            //@ts-ignore
            printParameters.pdfOptions.width = `${p2ReportDef.width}mm`;

            printParameters.pdfOptions.marginOptions = { bottom: '5mm', top: '5mm', left: '5mm', right: '5mm' };
        }
        if (p2ReportDef.custom_margins == 1) {
            printParameters.pdfOptions.marginOptions = {
                top: `${p2ReportDef.margin_top}mm`,
                right: `${p2ReportDef.margin_right}mm`,
                bottom: `${p2ReportDef.margin_bottom}mm`,
                left: `${p2ReportDef.margin_left}mm`,
            };
        }
        return await this.htmlToPdfBlob(htmlReport.htmlstring, printParameters);
    }

    public async printPDF(parameters: { reportKeyno: number; printerKeyno: number; copies?: number; parameters: Record<string, any> }): Promise<Record<string, string | number>> {
        const p2ReportDef = (await this.loadP2ReportDef(parameters.reportKeyno))[0];
        if (p2ReportDef?.errorcode && p2ReportDef.errorcode != 0) {
            return p2ReportDef;
        }

        const htmlReport = (await this.loadHtmlReportHtml(p2ReportDef.htmlreport_keyno, parameters.parameters))[0];
        if (htmlReport?.errorcode && htmlReport.errorcode != 0) {
            return htmlReport;
        }
        htmlReport.filename = this.base64.urlDecode(htmlReport.filename);
        htmlReport.headerstring = this.base64.urlDecode(htmlReport.headerstring);
        htmlReport.htmlstring = this.base64.urlDecode(htmlReport.htmlstring);
        htmlReport.footerstring = this.base64.urlDecode(htmlReport.footerstring);

        const printParameters = {
            printerKeyno: parameters.printerKeyno,
            filename: htmlReport.filename,
            saveToFileServer: true,
            sourceHtml: htmlReport.htmlstring,
            printerProperties: {
                isLandscape: p2ReportDef.isLandscape,
                copies: parameters.copies ?? 1,
                pageAutoSize: true, //p2ReportDef.paper_size_id === 'standard',
                //paperSize: {
                //    height: p2ReportDef.height ?? 0,
                //    width: p2ReportDef.width ?? 0
                //}
            },
            pdfOptions: {
                marginOptions: { bottom: '12.7mm', top: '12.7mm', left: '12.7mm', right: '12.7mm' },
                landscape: p2ReportDef.print_landscape == '1' ? true : false,
                outline: false,
                printBackground: true,
                displayHeaderFooter: true,
                headerTemplate: htmlReport.headerstring,
                footerTemplate: htmlReport.footerstring,
            },
        };
        if (p2ReportDef.paper_size_id !== 'standard' && p2ReportDef.height > 0 && p2ReportDef.width > 0) {
            //@ts-ignore
            printParameters.pdfOptions.height = `${p2ReportDef.height}mm`;
            //@ts-ignore
            printParameters.pdfOptions.width = `${p2ReportDef.width}mm`;

            printParameters.pdfOptions.marginOptions = { bottom: '5mm', top: '5mm', left: '5mm', right: '5mm' };
        }
        if (p2ReportDef.custom_margins == 1) {
            printParameters.pdfOptions.marginOptions = {
                top: `${p2ReportDef.margin_top}mm`,
                right: `${p2ReportDef.margin_right}mm`,
                bottom: `${p2ReportDef.margin_bottom}mm`,
                left: `${p2ReportDef.margin_left}mm`,
            };
        }
        //console.dir(p2ReportDef);
        //console.dir(printParameters);

        await this.printService.HtmlToPdf(htmlReport.htmlstring, printParameters);
        return { errorcode: 0, errormessage: '' };
    }

    public async printPDFBook(parameters: { reportKeyno: number; printerKeyno: number; parameters: Record<string, any>[]; saveToFileServer?: boolean }) {
        for (let parms of parameters.parameters) {
            await this.printPDF({
                printerKeyno: parameters.printerKeyno,
                reportKeyno: parameters.reportKeyno,
                parameters: parms,
            });
        }
        //parameters.saveToFileServer = true;
        //const a = document.createElement('a');
        //const url = await (this.previewPDFBook(parameters));
        //a.href = url;
        //a.download = `htmlbook.pdf`;
        //a.click();
    }

    public async previewPDFBook(
        parameters: {
            reportKeyno: number;
            printerKeyno?: number;
            parameters: Record<string, any>[];
            saveToFileServer?: boolean;
        },
        progress?: any,
    ) {
        const p2ReportDef = (await this.loadP2ReportDef(parameters.reportKeyno))[0];

        const pdfsToMerge: ArrayBuffer[] = [];

        if (progress) {
            progress.show();
        }

        for (const parms of parameters.parameters) {
            const htmlReport = (await this.loadHtmlReportHtml(p2ReportDef.htmlreport_keyno, parms))[0];

            htmlReport.headerstring = this.base64.urlDecode(htmlReport.headerstring);
            htmlReport.htmlstring = this.base64.urlDecode(htmlReport.htmlstring);
            htmlReport.footerstring = this.base64.urlDecode(htmlReport.footerstring);

            const printParameters = {
                download: true,
                filename: htmlReport.filename,
                sourceHtml: htmlReport.htmlstring,
                saveToFileServer: true,
                printerProperties: {
                    isLandscape: p2ReportDef.isLandscape,
                    copies: 1,
                    pageAutoSize: p2ReportDef.paper_size_id === 'standard',
                    paperSize: {
                        height: p2ReportDef.height ?? 0,
                        width: p2ReportDef.width ?? 0,
                    },
                },
                pdfOptions: {
                    marginOptions: { bottom: '12.7mm', top: '12.7mm', left: '12.7mm', right: '12.7mm' },
                    landscape: p2ReportDef.print_landscape == '1' ? true : false,
                    outline: false,
                    printBackground: true,
                    displayHeaderFooter: true,
                    headerTemplate: htmlReport.headerstring,
                    footerTemplate: htmlReport.footerstring,
                },
            };

            if (p2ReportDef.paper_size_id !== 'standard' && p2ReportDef.height > 0 && p2ReportDef.width > 0) {
                //@ts-ignore
                printParameters.pdfOptions.height = `${p2ReportDef.height}mm`;
                //@ts-ignore
                printParameters.pdfOptions.width = `${p2ReportDef.width}mm`;

                printParameters.pdfOptions.marginOptions = { bottom: '5mm', top: '5mm', left: '5mm', right: '5mm' };
            }
            if (p2ReportDef.custom_margins == 1) {
                printParameters.pdfOptions.marginOptions = {
                    top: `${p2ReportDef.margin_top}mm`,
                    right: `${p2ReportDef.margin_right}mm`,
                    bottom: `${p2ReportDef.margin_bottom}mm`,
                    left: `${p2ReportDef.margin_left}mm`,
                };
            }

            pdfsToMerge.push(await (await this.htmlToPdfBlob(htmlReport.htmlstring, printParameters)).binaryData.arrayBuffer());

            if (progress) {
                if (progress?.instance?.closed?.$$state?.status && progress.instance.closed.$$state.status === 1) {
                    return '';
                } else {
                    progress.step();
                }
            }
        }

        const mergedPdf = await PDFDocument.create();
        for (const pdfBytes of pdfsToMerge) {
            const pdf = await PDFDocument.load(pdfBytes);
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => {
                mergedPdf.addPage(page);
            });

            if (progress) {
                if (progress?.instance?.closed?.$$state?.status && progress.instance.closed.$$state.status === 1) {
                    return '';
                } else {
                    progress.step();
                }
            }
        }

        const buf = await mergedPdf.saveAsBase64({ dataUri: true });
        const response = await fetch(buf);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (progress) {
            progress.hide();
        }
        return url;
    }

    public async loadP2ReportDef(p2_reportdef_keyno: number) {
        return await this.dataTaskService.Post(334, {
            p2_reportdef_keyno: p2_reportdef_keyno,
        });
    }

    public async loadHtmlReportHtml(htmlreport_keyno: number, parameters: Record<string, any>) {
        return this.dataTaskService.Post(3370, {
            htmlreport_keyno: htmlreport_keyno,
            jsonwhere: JSON.stringify(parameters),
        });
    }

    /**
     * Converts the given html string to pdf.
     *
     * @param htmlString the html-string to convert to a pdf.
     * @returns a promise containg the base64 encoded of the pdf.
     */
    //@ts-ignore
    public convertHTMLToPDF(htmlString: string): Promise<string> {
        return this.printService.HtmlToPdfBase64(htmlString);
    }

    public htmlToPdf(
        htmlString: string,
        params: {
            sourceHtml?: string;
            sourceUrl?: string;
            filename?: string;
            saveToFileServer?: boolean;
            download?: boolean;
            getBase64Data?: boolean;
            urlEncodeBase64Data?: boolean;
            pdfOptions: Object;
        },
    ): Promise<string> {
        return this.printService.HtmlToPdf(htmlString, params);
    }

    public htmlToPdfBlob(
        htmlString: string,
        params: {
            sourceHtml?: string;
            sourceUrl?: string;
            filename?: string;
            saveToFileServer?: boolean;
            download?: boolean;
            getBase64Data?: boolean;
            urlEncodeBase64Data?: boolean;
            pdfOptions: Object;
        },
    ): Promise<IBase64Response> {
        return this.printService.HtmlToPdfBlob(htmlString, params);
    }

    private getTableOfContentsPageCount(entries: TableOfContentsEntry[]) {
        const bottomMargin = 72;
        const wrappedLineHeight = 12;
        let pageCount = 1;
        let top = this.A4_PAGE_HEIGHT - 127;

        entries.forEach((entry) => {
            if (top - (entry.lines.length - 1) * wrappedLineHeight < bottomMargin) {
                pageCount++;
                top = this.A4_PAGE_HEIGHT - 72;
            }

            top -= entry.height;
        });

        return pageCount;
    }

    /**
     * Merges the provided list of pdfs and/or images (only .jpeg, .jpg or .png.) to a single pdf document. The content can be provided with a base64 encoded string,
     * uint8array or array-buffer only.
     *
     * @param mergeDocuments list of pdf and/or images to merge together to a single pdf.
     * @returns a promise containing the base64 encoded string of the merged document.
     */
    public async mergePDFs(mergeDocuments: MergeDocument[], config?: Partial<{ includeTableOfContents: boolean; includeBookmarks: boolean; fileName?: string; template?: string; pageNumberConfig: PageNumberConfig }>): Promise<string> {
        const document = await PDFDocument.create();
        const includeTableOfContents = config?.includeTableOfContents ?? true;

        if (config?.template) {
            await this.loadTemplate(config?.template);
        } else {
            delete this.templateDoc;
        }

        if (!!config?.fileName && typeof config.fileName === 'string') {
            document.setTitle(config.fileName);
            document.setSubject(config.fileName);
        }

        for (let base64Pdf of mergeDocuments) {
            if (base64Pdf.contentType === 'image/jpg' || base64Pdf.contentType === 'image/jpeg' || base64Pdf.contentType === 'image/png') {
                const imagePdf = await this.createPDFFromImage(base64Pdf.content, base64Pdf.contentType);
                await this.addPDFToDocument(imagePdf, document, base64Pdf);
            } else if (base64Pdf.contentType === 'application/pdf') {
                await this.addPDFToDocument(base64Pdf.content, document, base64Pdf, includeTableOfContents);
            }
        }

        if (includeTableOfContents) {
            await this.generateTableOfContents(document, mergeDocuments, undefined, config?.pageNumberConfig);
        } else if (config?.pageNumberConfig?.showPageNumber) {
            for (let document of mergeDocuments) {
                if (document.index !== 0 || (document.index === 0 && config.pageNumberConfig.showPageNumberOnFirstPage)) {
                    this.addPageNumber({ pageRef: document.pageRef, pageNumber: document.index + 1, ...config.pageNumberConfig });
                }
            }
        }

        if (config?.includeBookmarks ?? includeTableOfContents) {
            this.generateBookmarks(document, mergeDocuments);
        }

        return await document.saveAsBase64({ dataUri: true });
    }

    public async createRotatedPdf(content: string | Uint8Array | ArrayBuffer, contentType: MergeDocument['contentType'], rotationDegrees: number): Promise<Uint8Array> {
        const pdfContent = contentType === 'application/pdf' ? content : await this.createPDFFromImage(content, contentType);
        const document = await PDFDocument.load(pdfContent);

        document.getPages().forEach((page) => this.rotatePage(page, rotationDegrees));

        return document.save();
    }

    private generateBookmarks(document: PDFDocument, mergeDocuments: MergeDocument[]) {
        const bookmarks = this.getBookmarkTree(document, mergeDocuments);

        if (bookmarks.length === 0) return;

        const outlinesRef = document.context.nextRef();
        const allBookmarks = this.flattenBookmarks(bookmarks);

        const registerBookmarks = (items: BookmarkNode[], parentRef: PDFRef) => {
            items.forEach((item, index) => {
                const previous = items[index - 1];
                const next = items[index + 1];
                const bookmark = document.context.obj({
                    Title: PDFHexString.fromText(item.title),
                    Parent: parentRef,
                    Dest: [item.pageRef.ref, 'Fit'],
                });

                if (previous) bookmark.set(PDFName.of('Prev'), previous.ref);
                if (next) bookmark.set(PDFName.of('Next'), next.ref);

                if (item.children.length > 0) {
                    bookmark.set(PDFName.of('First'), item.children[0].ref);
                    bookmark.set(PDFName.of('Last'), item.children[item.children.length - 1].ref);
                    bookmark.set(PDFName.of('Count'), PDFNumber.of(this.flattenBookmarks(item.children).length));
                }

                document.context.assign(item.ref, bookmark);
                registerBookmarks(item.children, item.ref);
            });
        };

        registerBookmarks(bookmarks, outlinesRef);
        document.context.assign(
            outlinesRef,
            document.context.obj({
                Type: 'Outlines',
                First: bookmarks[0].ref,
                Last: bookmarks[bookmarks.length - 1].ref,
                Count: PDFNumber.of(allBookmarks.length),
            }),
        );
        document.catalog.set(PDFName.of('Outlines'), outlinesRef);
        document.catalog.set(PDFName.of('PageMode'), PDFName.of('UseOutlines'));
    }

    private getBookmarkTree(document: PDFDocument, mergeDocuments: MergeDocument[]): BookmarkNode[] {
        const roots: BookmarkNode[] = [];
        const parents: BookmarkNode[] = [];

        for (const mergeDocument of mergeDocuments) {
            if (!mergeDocument.pageRef || !mergeDocument.pagetitle || mergeDocument.pagetitle === 'Frontpage') continue;

            const level = Math.min(Math.max(Number(mergeDocument.level) || 1, 1), 3);
            const bookmark: BookmarkNode = {
                title: mergeDocument.pagetitle,
                pageRef: mergeDocument.pageRef,
                ref: document.context.nextRef(),
                children: [],
            };

            let parent: BookmarkNode | undefined;
            for (let parentLevel = level - 1; parentLevel >= 1; parentLevel--) {
                if (parents[parentLevel - 1]) {
                    parent = parents[parentLevel - 1];
                    break;
                }
            }

            if (parent) {
                parent.children.push(bookmark);
            } else {
                roots.push(bookmark);
            }

            parents[level - 1] = bookmark;
            parents.length = level;
        }

        return roots;
    }

    private flattenBookmarks(bookmarks: BookmarkNode[]): BookmarkNode[] {
        return bookmarks.flatMap((bookmark) => [bookmark, ...this.flattenBookmarks(bookmark.children)]);
    }

    /**
     * Adds the provided pdf content to the provided pdf document.
     *
     * @param pdfContent the encoded pdf content to add to the pdf document. Will be added at the end of the document.
     * @param document the document to add the pdf content to.
     */
    private async addPDFToDocument(pdfContent: string | Uint8Array | ArrayBuffer, document: PDFDocument, mergeDocument: MergeDocument, includeTableOfContents: boolean = true) {
        const pdf = await PDFDocument.load(pdfContent);
        mergeDocument.index = document.getPageCount();

        for (const pageIndex of pdf.getPageIndices()) {
            const [page] = await document.copyPages(pdf, [pageIndex]);
            this.rotatePage(page, mergeDocument.rotationDegrees ?? 0);

            // Add the page
            let addedPage;
            //  = document.addPage(page);

            // Stamp template on top (or behind—see note below)
            if (mergeDocument.includeTemplate) {
                // await this.stampTemplate(document, addedPage);
                const srcWidth = page.getWidth();
                const srcHeight = page.getHeight();

                // ✅ Make the output page taller so content doesn't shrink
                const outPage = document.addPage([srcWidth, srcHeight]);

                // 1) Draw template first (so it behaves like background)
                await this.stampTemplate(document, outPage);

                // 2) Draw the source page content on top, shifted DOWN (leaves space at the top)
                const srcXObject = await document.embedPage(page);
                outPage.drawPage(srcXObject, {
                    x: 0,
                    y: 0, // keep bottom aligned
                    width: srcWidth, // same size => no scaling
                    height: srcHeight, // same size => no scaling
                });

                addedPage = outPage;
            } else {
                addedPage = document.addPage(page);
            }

            // Track first page ref for TOC linking (only set once)
            if (!mergeDocument.pageRef) {
                mergeDocument.pageRef = addedPage;
            }
        }
    }

    private templateDoc?: PDFDocument;
    private templatePageIndex = 0;

    /** Call once before merging pages */
    private async loadTemplate(templatePdf: Uint8Array | ArrayBuffer | string) {
        this.templateDoc = await PDFDocument.load(templatePdf);
    }

    /** Draw the template onto an existing page (as background) */
    private async stampTemplate(document: PDFDocument, page: PDFPage) {
        if (!this.templateDoc) return;

        // Copy the template page into the output doc (as a reusable XObject)
        const [tplPage] = await document.copyPages(this.templateDoc, [this.templatePageIndex]);
        const tplXObject = await document.embedPage(tplPage);

        const { width, height } = page.getSize();

        // Draw as background covering the whole page
        page.drawPage(tplXObject, {
            x: 0,
            y: 0,
            width,
            height,
        });
    }

    /**
     * Creates a pdf document from the provided base64 image of the given content-type. Returns a pdf document encoded in Uint8Array
     *
     * @param encodedImage the encoded image to create pdf from, can be either base64 encoded string, array-buffer or a uint8array.
     * @param contentType the content type of the image, needs to be either jpg, jpeg or png.
     * @returns a promise containing uint8array of the pdf document with the image.
     */
    private async createPDFFromImage(encodedImage: string | Uint8Array | ArrayBuffer, contentType: string): Promise<Uint8Array> {
        if (contentType === 'image/jpg' || contentType === 'image/jpeg' || contentType === 'image/png') {
            const document = await PDFDocument.create();
            let image;

            if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
                image = await document.embedJpg(encodedImage);
            } else if (contentType === 'image/png') {
                image = await document.embedPng(encodedImage);
            }

            if (image) {
                const widthA4 = PageSizes.A4[0];
                const heightA4 = PageSizes.A4[1];
                const isLandscape = image.width > image.height;

                let imageWidth = image.width;
                let imageHeight = image.height;

                if (isLandscape) {
                    imageWidth = heightA4;
                    imageHeight = Math.round((image.height / image.width) * imageWidth);
                } else {
                    imageHeight = widthA4;
                    imageWidth = Math.round((image.height / image.width) * imageHeight);
                }

                const page = document.addPage([imageWidth, imageHeight]);
                page.drawImage(image, {
                    x: 0,
                    y: 0,
                    width: imageWidth,
                    height: imageHeight,
                });

                // const page = document.addPage([image.width, image.height]);
                // page.drawImage(image, {
                //     x: 0,
                //     y: 0,
                //     width: image.width,
                //     height: image.height,
                // });
            }

            return await document.save();
        }

        throw Error('Unsupported content type. Only .jpeg, .jpg and .png are supported image formats.');
    }

    private rotatePage(page: PDFPage, rotationDegrees: number) {
        const requestedRotation = this.normalizeRotation(rotationDegrees);

        if (requestedRotation === 0) return;

        page.setRotation(degrees(this.normalizeRotation(page.getRotation().angle + requestedRotation)));
    }

    private normalizeRotation(rotationDegrees: number): number {
        const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;

        return [0, 90, 180, 270].includes(normalizedRotation) ? normalizedRotation : 0;
    }

    /**
     * Generates a table of contents for the given pdf-document, indexing the given merge-documents.
     *
     * @param document the pdf-document to create a table of contents for.
     * @param mergeDocuments the merged documents to index in the table of contents.
     * @param indexHeading the heading to use for the first page of the table of contents page(s).
     */
    private async generateTableOfContents(document: PDFDocument, mergeDocuments: MergeDocument[], indexHeading: string = 'Table of Contents', pageNumberConfig?: PageNumberConfig) {
        const entries = await this.getTableOfContentsEntries(document, mergeDocuments);
        const tableOfContentsPageCount = this.getTableOfContentsPageCount(entries);
        let pages = [document.insertPage(1, [this.A4_PAGE_WIDTH, this.A4_PAGE_HEIGHT])];
        pages[0].drawText(indexHeading, {
            x: 56,
            y: this.A4_PAGE_HEIGHT - 72,
            size: 18,
            color: rgb(0, 0, 0),
        });

        let top = this.A4_PAGE_HEIGHT - 127;
        let currentPageIndex = 0;

        const frontpage = mergeDocuments.find((mergeDocument) => mergeDocument.pagetitle === 'Frontpage');

        if (frontpage && pageNumberConfig?.showPageNumber && pageNumberConfig?.showPageNumberOnFirstPage) {
            this.addPageNumber({ pageRef: frontpage.pageRef, pageNumber: frontpage.index + 1 });
        }

        for (const entry of entries) {
            if (top - (entry.lines.length - 1) * 12 < 72) {
                pages.push(document.insertPage(currentPageIndex + 2, [this.A4_PAGE_WIDTH, this.A4_PAGE_HEIGHT]));
                currentPageIndex++;
                top = this.A4_PAGE_HEIGHT - 72;
            }

            const mergeDocument = entry.mergeDocument;

            const linkAnnotationRef = await this.createAnnotations({
                document: document,
                page: pages[currentPageIndex],
                pageNumber: mergeDocument.index + 1 + tableOfContentsPageCount,
                top: top,
                ref: mergeDocument.pageRef,
                lines: entry.lines,
                left: entry.left,
                font: entry.font,
                height: entry.height,
            });

            if (pageNumberConfig?.showPageNumber) {
                this.addPageNumber({ pageRef: mergeDocument.pageRef, pageNumber: mergeDocument.index + 1 + tableOfContentsPageCount, color: pageNumberConfig?.color, fontSize: pageNumberConfig?.fontSize, x: pageNumberConfig?.x, y: pageNumberConfig?.y });
                // mergeDocument.pageRef.drawText('' + (mergeDocument.index + 1 + this.getTableOfContentsPageCount(mergeDocuments.length)), { y: 20, x: this.A4_PAGE_WIDTH - 30, color: rgb(1, 1, 1), size: 10 });
            }

            top -= entry.height;

            const annots = pages[currentPageIndex].node.lookup(PDFName.of('Annots'), PDFArray);
            annots.push(linkAnnotationRef);
        }
    }

    private async getTableOfContentsEntries(document: PDFDocument, mergeDocuments: MergeDocument[]): Promise<TableOfContentsEntry[]> {
        const size = 10;
        const pageNumberLeft = this.A4_PAGE_WIDTH - 72;
        const fonts = {
            1: await document.embedFont(StandardFonts.HelveticaBold),
            2: await document.embedFont(StandardFonts.Helvetica),
            3: await document.embedFont(StandardFonts.HelveticaOblique),
        };

        return mergeDocuments
            .filter((mergeDocument) => mergeDocument.pagetitle !== 'Frontpage')
            .map((mergeDocument) => {
                const level = ([1, 2, 3].includes(Number(mergeDocument.level)) ? Number(mergeDocument.level) : 1) as 1 | 2 | 3;
                const left = 72 + (level - 1) * 12;
                const font = fonts[level];
                const lines = this.wrapText(mergeDocument.pagetitle, font, size, pageNumberLeft - left - 12);

                return {
                    mergeDocument,
                    lines,
                    left,
                    font,
                    height: 20 + (lines.length - 1) * 12,
                };
            });
    }

    private wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
        const lines: string[] = [];

        text.split(/\r?\n/).forEach((paragraph) => {
            const words = paragraph.trim().split(/\s+/).filter(Boolean);
            let line = '';

            words.forEach((word) => {
                const candidate = line ? `${line} ${word}` : word;

                if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
                    line = candidate;
                    return;
                }

                if (line) lines.push(line);

                const parts = this.splitWordToWidth(word, font, size, maxWidth);
                lines.push(...parts.slice(0, -1));
                line = parts[parts.length - 1];
            });

            lines.push(line);
        });

        return lines.length > 0 ? lines : [''];
    }

    private splitWordToWidth(word: string, font: PDFFont, size: number, maxWidth: number): string[] {
        const parts: string[] = [];
        let part = '';

        for (const character of word) {
            if (part && font.widthOfTextAtSize(part + character, size) > maxWidth) {
                parts.push(part);
                part = character;
            } else {
                part += character;
            }
        }

        parts.push(part);

        return parts;
    }

    private addPageNumber(params: { pageRef: PDFPage; pageNumber: number } & PageNumberConfig) {
        params.pageRef.drawText('' + params.pageNumber, { y: params.y ?? 0, x: params.x ?? 0, color: this.getColor(params.color), size: params.fontSize ?? 10 });
    }

    private getColor(color: unknown): RGB {
        if (!!color && typeof color === 'string' && color.startsWith('#')) {
            console.log(color);
            let r = 0;
            let g = 0;
            let b = 0;

            if (color.length > 4) {
                r = parseInt(color.substring(1, 3), 16);
                g = parseInt(color.substring(3, 5), 16);
                b = parseInt(color.substring(5), 16);
            } else {
                r = parseInt(color.substring(1, 2), 16);
                g = parseInt(color.substring(2, 3), 16);
                b = parseInt(color.substring(3, 4), 16);
            }

            return rgb(r / 255, g / 255, b / 255);
        } else if (!!color && typeof color === 'string' && color.startsWith('rgb(')) {
            const rgbString = color.replace('rgb', '').replace('a', '').replace('(', '').replace(')', '').split(',');
            const r = +rgbString[0];
            const g = +rgbString[1];
            const b = +rgbString[2];
            return rgb(r / 255, g / 255, b / 255);
        } else if (!!color && typeof color === 'object' && 'red' in color && typeof color.red === 'number' && 'green' in color && typeof color.green === 'number' && 'blue' in color && typeof color.blue === 'number') {
            return color as RGB;
        } else {
            return rgb(0, 0, 0);
        }
    }

    /**
     * Creates an annotation for the given document, on the given page. The annotation will be displayed using the given linktext and page-number. The given top
     * decides the y-coordinate on the page the annotation should be places (in PDF's 1 is bottom and the page height is the top). The annotation will be styled according to the provided
     * level. The annotation creates a destination link to the ref which is a pdf-page.
     *
     * @param param0 the annotation configuration to use for creating an annotation.
     * @returns a promise containing a pdf-reference to the created annotation.
     */
    private async createAnnotations({ document, page, pageNumber, top, ref, lines, left, font, height }: CreateAnnotationsParameters): Promise<PDFRef> {
        const size = 10;
        const right = this.A4_PAGE_WIDTH;
        const lastLineTop = top - (lines.length - 1) * 12;

        const linkAnnotation = document.context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: [left, top - height + 8, right - 56, top + size],
            Border: [0, 0, 0],
            C: [0, 0, 0],
            Dest: [ref.ref, 'XYZ', null, this.A4_PAGE_HEIGHT, null],
            // Dest: [document.getPage(index).ref, 'XYZ', null, 1, null],
        });

        lines.forEach((line, index) => {
            page.drawText(line, {
                x: left,
                y: top - index * 12,
                size: size,
                font: font,
                color: rgb(0, 0, 0),
            });
        });

        page.drawText(`${pageNumber}`, {
            x: right - 72,
            y: top,
            size: size,
            font: font,
            color: rgb(0, 0, 0),
        });

        page.drawLine({
            start: { x: left, y: lastLineTop - 2 },
            end: { x: right - 56, y: lastLineTop - 3 },
            thickness: 1,
            color: rgb(0, 0, 1),
            opacity: 0.75,
        });

        return document.context.register(linkAnnotation);
    }

    public async isPdfEncrypted(arrayBuffer: ArrayBuffer) {
        try {
            const uint8Array = new Uint8Array(arrayBuffer);
            await PDFDocument.load(uint8Array);
            return false;
        } catch (error) {
            if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message.includes('encrypted')) {
                return true;
            }
            // Optional: Rethrow or handle other syntax errors if the file is corrupted
            return false;
        }
    }
}

/**
 * Represents an object with properties required for merging.
 */
export interface MergeDocument {
    /**
     * The base64 encoded content, or uint8array or arraybuffer, containing the pdf, jpeg og png.
     */
    content: string | Uint8Array | ArrayBuffer;

    /**
     * The content-type of the content, etiher pdf, jpeg or png.
     */
    contentType: 'image/jpg' | 'image/jpeg' | 'image/png' | 'application/pdf';

    /**
     * The page title of the document.
     */
    pagetitle: string;

    /**
     * The index where this document was added to the pdf-document. Does not include table of contents.
     */
    index: number;

    /**
     * The hierarchy level to use for the page title in the table of contents and PDF bookmarks.
     */
    level: AnnotationLevel;

    /**
     * Reference to the page added to the pdf document.
     */
    pageRef: PDFPage;

    includeTemplate?: boolean;

    rotationDegrees?: number;
}

interface BookmarkNode {
    title: string;
    pageRef: PDFPage;
    ref: PDFRef;
    children: BookmarkNode[];
}

interface TableOfContentsEntry {
    mergeDocument: MergeDocument;
    lines: string[];
    left: number;
    font: PDFFont;
    height: number;
}

/**
 * Represents paramaeters required for creating an annotation.
 */
interface CreateAnnotationsParameters {
    /**
     * The pdf document to add the annotation on.
     */
    document: PDFDocument;

    /**
     * The page to add the annotation to.
     */
    page: PDFPage;

    /**
     * The actual page number (not zero-based and including table of contents) for the destination of the annotation. Displayed right side of the link text.
     */
    pageNumber: number;

    /**
     * The y-position to use for placing the annotation.
     */
    top: number;

    /**
     * Reference to the page to navigate to.
     */
    ref: PDFPage;

    lines: string[];

    left: number;

    font: PDFFont;

    height: number;
}

/**
 * The level of the document (level 1, level 2 or level 3), relates to how the document is formatted in the table of contents (think heading 1, heading 2 etc. in a regular word document).
 */
type AnnotationLevel = '1' | '2' | '3';

// const template =
//     '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';

interface PageNumberConfig {
    /**
     * @default 0
     */
    x?: number;

    /**
     * @default 0
     */
    y?: number;

    /**
     * @default false
     */
    showPageNumber?: boolean;

    /**
     * @default false
     */
    showPageNumberOnFirstPage?: boolean;

    /**
     * @default rgb(0,0,0)
     */
    color?: 'string' | RGB | undefined;

    /**
     * @default 10
     */
    fontSize?: number;
}
